import React, { useCallback, useRef } from "react";
import {
  AgGrid,
  Button,
  Card,
  Flex,
  HelpIndicator,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Text,
  useCopy,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { PaymentStatus } from "#graphql/supergraphEnums";
import { toDbId } from "#utils/graphql_utils";
import { PAYMENT_REFERENCE_TOOLTIP_TEXT } from "../../constants";
import { PaymentFailedBanner } from "../../PayableWorkflow/components/PaymentFailedBanner";
import { PositionRowItem } from "../types";
import { detailCard, detailContainer } from "./styles_vanilla.css";

export type PaymentReferenceDetailRendererParams = AgGrid.ICellRendererParams<PositionRowItem>;

export const PaymentReferenceDetailRenderer = (
  params: PaymentReferenceDetailRendererParams,
) => {
  const { copy, attemptedCopy } = useCopy({ useTimeout: true });
  const referenceNumber = params.data?.referenceNumber;
  const paymentStatus = params.data?.status;
  const failedPaymentDetails = params.data?.failedPaymentDetails;
  const accountId = params.data?.accountId;
  const hostName = params.data?.hostName;
  const tooltipIdRef = useRef(`tooltip-${Math.random().toString(36).slice(2, 11)}`);
  const tooltipId = tooltipIdRef.current;

  const handleCopy = useCallback(() => {
    if (referenceNumber) {
      void copy(referenceNumber);
    }
  }, [copy, referenceNumber]);

  const isPaymentFailed = paymentStatus === PaymentStatus.Failed;

  // Construct the update instructions URL if we have the necessary data
  const updateInstructionsUrl =
    accountId && hostName ? `https://${hostName}/account/${toDbId(accountId)}` : null;

  const showBanner = isPaymentFailed && !!failedPaymentDetails;

  if (!referenceNumber && !showBanner) {
    return null;
  }

  return (
    <Flex className={detailContainer} data-testid="payment-reference-detail">
      <Card className={detailCard} padding="sm">
        {showBanner && updateInstructionsUrl && (
          <PaymentFailedBanner
            failedPaymentDetails={failedPaymentDetails}
            updateInstructionsUrl={updateInstructionsUrl}
          />
        )}
        {referenceNumber && (
          <Table condensed>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Flex alignItems="center" gap={16}>
                    <Flex alignItems="center" gap={4}>
                      <Text variant="secondary-md">Payment reference number</Text>
                      <HelpIndicator
                        id={tooltipId}
                        placement="top"
                        content={PAYMENT_REFERENCE_TOOLTIP_TEXT}
                      >
                        <Icon type="info-circle" variant="outline" color={Colors.Neutral600} />
                      </HelpIndicator>
                    </Flex>
                    <Flex alignItems="center" gap={8}>
                      <Text variant="regular-md">{referenceNumber}</Text>
                      <Button
                        icon={attemptedCopy ? "check" : "copy"}
                        variant="muted"
                        onClick={handleCopy}
                        aria-label="Copy payment reference"
                      />
                    </Flex>
                  </Flex>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </Card>
    </Flex>
  );
};
